import dagre from '@dagrejs/dagre'
import type { Edge, Node } from '@xyflow/react'
import { isArray } from 'lodash'
import { Answer, Question } from '../../../Types'
import { isBranching } from './dependencyUtils'

export const BRANCH_COLORS = ['#1677FF', '#0A6B13', '#52C41A', '#61C8B1', '#9254DE']

export const getBranchColor = (optionIndex: number): string => BRANCH_COLORS[optionIndex % BRANCH_COLORS.length]

// ------------------------
export const NODE_WIDTH = 400
const NODE_BASE_HEIGHT = 200

export const getQuestionOptions = (question: Question, defaultLang: string): string[] => {
  const options: any[] | undefined = question.options
  if (!options || options.length === 0 || !isArray(options)) return []
  return options.map((opt, i) => opt[defaultLang] || `Option ${i + 1}`)
}

// ReactFlow draws the connecting lines between questions on the graph.
// this is called edges
//
// For a branching question (like a radio), one line goes out per option —
// each line coloured by which branch it represents. If a particular option
// doesn't have its own "next" question set, it falls back to the question's
// default next. If the default itself points to a question that no longer
// exists in the assignment, we skip drawing the edge rather than draw a
// line to nowhere.
//
// For a plain (non-branching) question, we just draw a single grey line
// straight to whatever question comes next.
export const buildEdges = (questions: Question[], defaultLang: string): Edge[] => {
  const edges: Edge[] = []

  for (const q of questions) {
    if (!q.next || q.next.length === 0) continue

    if (isBranching(q.type)) {
      const options = getQuestionOptions(q, defaultLang)
      const kk = options?.length || 1
      const defaultTarget = q.next.find((n) => n !== null) || null

      // Some questionnaires reference question ids that no longer exist.
      // Treat a broken default as null so we don't draw phantom edges.
      const defaultExist: string | null = questions.some((qq) => qq._id === defaultTarget) ? defaultTarget : null

      for (let i = 0; i < kk; i++) {
        const considerNextId = defaultExist && q.next[i] !== undefined
        const targetId = considerNextId ? q.next[i] : defaultExist
        if (!targetId) continue

        edges.push({
          id: `${q._id}-${targetId}-${i}`,
          source: q._id,
          sourceHandle: `option-${i}`,
          target: targetId,
          targetHandle: 'target',
          type: 'smoothstep',
          style: { stroke: getBranchColor(i), strokeWidth: 2 },
        })
      }
    } else {
      const targetId = q.next[0] ?? null
      if (!targetId) continue

      edges.push({
        id: `${q._id}-${targetId}-0`,
        source: q._id,
        sourceHandle: 'source',
        target: targetId,
        targetHandle: 'target',
        type: 'smoothstep',
        style: { stroke: '#8C8C8C', strokeWidth: 1.5 },
      })
    }
  }

  return edges
}

//
// THis is the build in layout engine for the react flow
// places each question card on the screen so nothing
// overlaps and the flow reads top-to-bottom (or left-to-right if you ask
// for it via the `direction` argument).
//
// Dagre — the layout engine — handles the actual positioning math. We just
// tell it how big each question card is (cards grow taller when they have
// more answer options to show) and which questions link to which. Dagre
// hands back x/y coordinates for every node, which we then apply.
//
// One quirk worth knowing: if the assignment has isolated questions with
// no incoming or outgoing lines, dagre would pile them into a single row.
// We stitch them together with invisible edges so they stack vertically
// like the rest of the graph.
//
export const applyDagreLayout = (
  nodes: Node[],
  edges: Edge[],

  direction: 'TB' | 'LR' = 'TB'
): { nodes: Node[]; edges: Edge[] } => {
  const g = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}))

  g.setGraph({ rankdir: direction, nodesep: 120, ranksep: 45 })

  const heightFor = (answers: Answer[]): number => {
    const lng = answers.length === 1 ? 0 : answers.length
    return NODE_BASE_HEIGHT + lng * 50
  }

  for (const node of nodes) {
    g.setNode(node.id, { width: NODE_WIDTH, height: heightFor(node.data.answers as Answer[]) })
  }

  for (const edge of edges) {
    g.setEdge(edge.source, edge.target)
  }

  // Chain unconnected nodes so dagre doesn't pile them in one row.
  const connectedIds = new Set<string>()
  for (const edge of edges) {
    connectedIds.add(edge.source)
    connectedIds.add(edge.target)
  }
  const unconnected = nodes.filter((n) => !connectedIds.has(n.id))
  for (let i = 0; i < unconnected.length - 1; i++) {
    g.setEdge(unconnected[i].id, unconnected[i + 1].id)
  }

  dagre.layout(g)

  const layoutedNodes = nodes.map((node) => {
    const pos = g.node(node.id)
    const height = heightFor(node.data.answers as Answer[])

    return {
      ...node,
      position: {
        x: pos.x - NODE_WIDTH / 2,
        y: pos.y - height / 2,
      },
    }
  })

  return { nodes: layoutedNodes, edges }
}
